import React, { useState, useEffect } from 'react';
import { DeveloperUser, DeveloperUserType, CreateDeveloperUserRequest, UpdateDeveloperUserRequest } from '../../types/DeveloperUser';
import { developerUserApi } from '../../services/developerUserApi';

/**
 * Administrasjon av felles utviklerbrukere (WMS_DeveloperUsers i master).
 * Brukerne kan logge inn på alle aktive tenants via WMS API-ets fallback-innlogging,
 * med Administrator-rolle og mulighet for impersonering («se som bruker»).
 */
const DeveloperUserManagement: React.FC = () => {
  const [users, setUsers] = useState<DeveloperUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState<DeveloperUser>({} as DeveloperUser);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newUserData, setNewUserData] = useState<CreateDeveloperUserRequest>({
    username: '',
    displayName: '',
    userType: 'Developer',
    password: '',
  });
  const [changingPasswordUser, setChangingPasswordUser] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState<string>('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await developerUserApi.getAll();
      setUsers(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching developer users:', err);
      setError('Feil ved henting av utviklerbrukere. Er WMS_DeveloperUsers-tabellen opprettet i master?');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEditStart = (user: DeveloperUser) => {
    setEditingUser(user.id);
    setEditFormData({ ...user });
    setChangingPasswordUser(null);
  };

  const handleEditCancel = () => {
    setEditingUser(null);
    setEditFormData({} as DeveloperUser);
  };

  const handleEditSave = async () => {
    if (!editingUser) return;

    try {
      const updateData: UpdateDeveloperUserRequest = {
        username: editFormData.username,
        displayName: editFormData.displayName,
        userType: editFormData.userType,
        isActive: editFormData.isActive,
      };

      await developerUserApi.update(editingUser, updateData);
      setEditingUser(null);
      setEditFormData({} as DeveloperUser);
      await fetchUsers();
    } catch (err) {
      console.error('Error updating developer user:', err);
      setError('Feil ved oppdatering av utviklerbruker');
    }
  };

  const handleFieldChange = (field: keyof DeveloperUser, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNewUserStart = () => {
    setIsCreatingNew(true);
    setEditingUser(null);
    setChangingPasswordUser(null);
  };

  const handleNewUserCancel = () => {
    setIsCreatingNew(false);
    setNewUserData({ username: '', displayName: '', userType: 'Developer', password: '' });
  };

  const handleNewUserSave = async () => {
    if (!newUserData.username.trim() || !newUserData.displayName.trim() || newUserData.password.length < 8) {
      setError('Brukernavn, visningsnavn og passord (minst 8 tegn) er påkrevd');
      return;
    }

    try {
      await developerUserApi.create(newUserData);
      setIsCreatingNew(false);
      setNewUserData({ username: '', displayName: '', userType: 'Developer', password: '' });
      setError(null);
      await fetchUsers();
    } catch (err) {
      console.error('Error creating developer user:', err);
      setError('Feil ved opprettelse av utviklerbruker');
    }
  };

  const handleNewUserFieldChange = (field: keyof CreateDeveloperUserRequest, value: string) => {
    setNewUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChangeStart = (userId: number) => {
    setChangingPasswordUser(userId);
    setNewPassword('');
    setEditingUser(null);
  };

  const handlePasswordChangeCancel = () => {
    setChangingPasswordUser(null);
    setNewPassword('');
  };

  const handlePasswordChangeSave = async () => {
    if (!changingPasswordUser) return;
    if (newPassword.length < 8) {
      setError('Passordet må være minst 8 tegn');
      return;
    }

    try {
      await developerUserApi.setPassword(changingPasswordUser, { newPassword });
      setChangingPasswordUser(null);
      setNewPassword('');
      setError(null);
    } catch (err) {
      console.error('Error updating password:', err);
      setError('Feil ved oppdatering av passord');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Er du sikker på at du vil slette denne utviklerbrukeren? Vurder å deaktivere i stedet.')) {
      try {
        await developerUserApi.remove(id);
        await fetchUsers();
      } catch (err) {
        console.error('Error deleting developer user:', err);
        setError('Feil ved sletting av utviklerbruker');
      }
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('nb-NO');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Laster utviklerbrukere...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full">
      {/* Info + Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        Brukerne her kan logge inn på <strong>alle aktive tenants</strong> i dette miljøet
        (WMS-innlogging: kundenr + brukernavn + passord). Passord lagres BCrypt-hashet.
        Type <strong>Utvikler</strong> = Administrator-rolle + impersonering («se som bruker»);
        type <strong>Integrasjon</strong> = Operatør-rolle uten impersonering, for maskinbrukere
        som eSENTRAL (kan angi <code>?companyNo=</code> per kall i multi-selskap-tenants).
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 font-medium ml-4">Lukk</button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Utviklerbrukere ({users.length})</h2>
        <button
          onClick={handleNewUserStart}
          disabled={isCreatingNew}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ny Utviklerbruker
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 w-full">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brukernavn</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visningsnavn</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Passord</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktiv</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sist innlogget</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opprettet</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Handlinger</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* New User Row */}
            {isCreatingNew && (
              <tr className="bg-green-50 border-2 border-green-200">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span className="text-green-600 font-medium">NY</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="text"
                    value={newUserData.username}
                    onChange={(e) => handleNewUserFieldChange('username', e.target.value)}
                    placeholder="Brukernavn"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="text"
                    value={newUserData.displayName}
                    onChange={(e) => handleNewUserFieldChange('displayName', e.target.value)}
                    placeholder="Visningsnavn"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <select
                    value={newUserData.userType}
                    onChange={(e) => setNewUserData(prev => ({ ...prev, userType: e.target.value as DeveloperUserType }))}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="Developer">Utvikler</option>
                    <option value="Integration">Integrasjon</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="password"
                    value={newUserData.password}
                    onChange={(e) => handleNewUserFieldChange('password', e.target.value)}
                    placeholder="Passord (minst 8 tegn)"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Ja</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-3">
                    <button onClick={handleNewUserSave} className="text-green-600 hover:text-green-800 transition-colors" title="Lagre ny utviklerbruker">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button onClick={handleNewUserCancel} className="text-gray-600 hover:text-gray-800 transition-colors" title="Avbryt">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {users.map((user) => {
              const isEditing = editingUser === user.id;

              return (
                <tr key={user.id} className={`transition-colors ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.username || ''}
                        onChange={(e) => handleFieldChange('username', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="text-sm font-medium text-gray-900">{user.username}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.displayName || ''}
                        onChange={(e) => handleFieldChange('displayName', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{user.displayName}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isEditing ? (
                      <select
                        value={editFormData.userType || 'Developer'}
                        onChange={(e) => handleFieldChange('userType', e.target.value as DeveloperUserType)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Developer">Utvikler</option>
                        <option value="Integration">Integrasjon</option>
                      </select>
                    ) : (
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${user.userType === 'Integration' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                        {user.userType === 'Integration' ? 'Integrasjon' : 'Utvikler'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {changingPasswordUser === user.id ? (
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Nytt passord (minst 8 tegn)"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    ) : (
                      <button
                        onClick={() => handlePasswordChangeStart(user.id)}
                        className="text-orange-600 hover:text-orange-800 text-sm transition-colors"
                        title="Endre passord"
                      >
                        Endre passord
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={editFormData.isActive || false}
                        onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    ) : (
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {user.isActive ? 'Aktiv' : 'Deaktivert'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.lastLoginDate)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.createdDate)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-3">
                      {changingPasswordUser === user.id ? (
                        <>
                          <button onClick={handlePasswordChangeSave} className="text-orange-600 hover:text-orange-800 transition-colors" title="Lagre nytt passord">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button onClick={handlePasswordChangeCancel} className="text-gray-600 hover:text-gray-800 transition-colors" title="Avbryt passord-endring">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : isEditing ? (
                        <>
                          <button onClick={handleEditSave} className="text-green-600 hover:text-green-800 transition-colors" title="Lagre">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button onClick={handleEditCancel} className="text-gray-600 hover:text-gray-800 transition-colors" title="Avbryt">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => handleEditStart(user)} className="text-blue-600 hover:text-blue-800 transition-colors" title="Rediger">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button onClick={() => handleDelete(user.id)} className="text-red-600 hover:text-red-800 transition-colors" title="Slett">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {users.length === 0 && !isCreatingNew && (
        <div className="text-center py-12">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-gray-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ingen utviklerbrukere funnet</h3>
          <p className="text-gray-500">Klikk på "Ny Utviklerbruker" for å legge til den første.</p>
        </div>
      )}
    </div>
  );
};

export default DeveloperUserManagement;
